import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { AppBar, Autocomplete, Dialog, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import { isEqual } from "lodash";

import DecreaseButton from "components/DecreaseButton";
import { FilterAppBar } from "components/FilterAppBar";
import FullScreenDialog from "components/FullScreenDialog";
import IncreaseButton from "components/IncreaseButton";
import { PartAutocomplete } from "components/parts/PartAutocomplete";
import { PartFilterDialog } from "components/parts/PartFilterDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import { useDebounce } from "hooks/useDebounce";
import { defaultPartFilter, PartFilter, PartFilterKeys } from "models/PartFilter";
import { StockSync } from "models/StockSync";
import { PartType, Stock } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { searchOrderStock, updateStockToOrder } from "store/slices/stock.store";

interface SelectStockDialogProps {
  open: boolean;
  handleClose: () => void;
  setOpenAddPartDialog: (open: boolean) => void;
}

export const SelectStockToOrderDialog: FC<SelectStockDialogProps> = (props) => {
  const { open, handleClose, setOpenAddPartDialog } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { orderStock, loadingOrderStock, stockList } = useAppSelector((state) => state.stock);
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const [tempQty, setTempQty] = useState<number>(1);
  const [qtyError, setQtyError] = useState<string>("");
  const [partList, setPartList] = useState<PartType[]>([]);
  const [scannerValue, setScannerValue] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState<PartFilter>(defaultPartFilter);
  const [filter, setFilter] = useState<PartFilter>(defaultPartFilter);
  const [partSearch, setPartSearch] = useState<string>("");
  const debouncedPartSearch = useDebounce<string>(partSearch, 500);
  const [selectedStock, setSelectedStock] = useState<Stock>({
    id: "",
    name: "",
  });

  const filterActiveCount = useCallback(() => {
    let count = 0;
    for (const k of PartFilterKeys) {
      if (isEqual(defaultPartFilter[k], appliedFilter[k])) continue;
      count++;
    }
    return count;
  }, [appliedFilter]);

  const applyFilters = useCallback(
    (partList: PartType[]) => {
      let newPartList = partList;
      if (appliedFilter) {
        if (!!appliedFilter.selectedSuppliers?.length) {
          newPartList = newPartList.filter((p) =>
            appliedFilter.selectedSuppliers.map((s) => s.id).includes(p.supplierId!),
          );
        }
        if (!!appliedFilter.sortGroup1?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup1.findIndex((x) => x.code === p.sortGroup1) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup2?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup2.findIndex((x) => x.code === p.sortGroup2) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup3?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup3.findIndex((x) => x.code === p.sortGroup3) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup4?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup4.findIndex((x) => x.code === p.sortGroup4) !== -1,
          );
        }
        if (!!appliedFilter.sortGroup5?.length) {
          newPartList = newPartList.filter(
            (p) => appliedFilter.sortGroup5.findIndex((x) => x.code === p.sortGroup5) !== -1,
          );
        }
      }
      return newPartList;
    },
    [appliedFilter],
  );

  const setAvailableParts = useCallback(() => {
    let newPartList: PartType[] = [];
    newPartList = orderStock;

    if (appliedFilter) {
      newPartList = applyFilters(newPartList);
    }
    setPartList(newPartList);
  }, [orderStock, appliedFilter, applyFilters]);

  const changeSelectedPart = useCallback((selectedPart: PartType | null) => {
    setSelectedPart(selectedPart);
    if (selectedPart !== null) {
    } else {
      setTempQty(0);
    }
  }, []);

  const changeQtyFromInput = useCallback(
    (value: string) => {
      const numericValue = parseInt(value);
      const maxLimit = selectedPart?.quantity ?? 0;

      if (numericValue <= maxLimit) {
        setTempQty(numericValue);
        setQtyError("");
      } else {
        if (numericValue > maxLimit) {
          setQtyError(
            intl.formatMessage(
              {
                id: "part.quantityTooHigh",
              },
              {
                qty: maxLimit,
              },
            ),
          );
        }
      }
    },
    [intl, selectedPart?.quantity],
  );

  const addStockCb = useCallback(() => {
    if (!selectedPart || !selectedPart.articleNo) return;

    const stockSync: StockSync = {
      part: selectedPart,
      sync: {
        articleNo: selectedPart.articleNo,
        quantity: tempQty,
        moveToTechnicianGuid: null,
      },
      moveToTech: null,
    };

    dispatch(updateStockToOrder({ stock: stockSync }));

    setSelectedPart(null);
    dispatch(
      addSnackbarMessage({
        key: "AddStock-success",
        argument: selectedPart.articleNo ?? "",
      }),
    );
  }, [dispatch, tempQty, selectedPart]);

  const closeDialog = useCallback(() => {
    setOpenAddPartDialog(false);
  }, [setOpenAddPartDialog]);

  const allStock: Stock = {
    id: "",
    name: intl.formatMessage({ id: "stock.allStocks" }),
  };

  useEffect(() => {
    if (open) {
      setAvailableParts();
    }
  }, [open, setAvailableParts]);

  useEffect(() => {
    if (scannerValue) {
      const scanMatches = partList.filter(
        (part) => part.barcode === scannerValue || part.articleNo === scannerValue,
      );

      if (scanMatches.length === 1) {
        setSelectedPart(scanMatches[0]);
      } else {
        dispatch(addSnackbarMessage({ key: "No-barcode-match", argument: scannerValue }));
      }
      setScannerValue("");
    }
  }, [scannerValue, intl, partList, dispatch]);

  useEffect(() => {
    if (open && (debouncedPartSearch.length !== 0 || selectedStock))
      dispatch(
        searchOrderStock({
          locationId: selectedStock.id,
          searchString: debouncedPartSearch,
          isStockTracing: true,
        }),
      );
  }, [debouncedPartSearch, dispatch, open, selectedStock, selectedStock.id]);

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="SelectStockToOrderDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="SelectStockToOrderDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            <FormattedMessage id="stock.orderStock" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer data-testid="SelectStockDialog">
        <FilterAppBar
          onClick={setFilterDialogOpen}
          filterCount={filterActiveCount()}
          foundCount={partList.length}
          type="parts"
        />
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={[allStock].concat(stockList)}
              getOptionLabel={(option) => `${option?.name}`}
              renderInput={(params) => (
                <StyledTextField {...params} label={intl.formatMessage({ id: "stock.stocks" })} />
              )}
              value={selectedStock}
              onChange={(_, value) => {
                if (value) {
                  setSelectedStock(value);
                } else {
                  setSelectedStock({
                    id: "",
                    name: "",
                  });
                }
              }}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          </Grid>
          <Grid item>
            <PartAutocomplete
              selectedPart={selectedPart}
              partList={partList}
              loading={loadingOrderStock}
              showQty
              partSearch={partSearch}
              setPartSearch={setPartSearch}
              setScannerValue={setScannerValue}
              changeSelectedPart={changeSelectedPart}
            />
          </Grid>

          {selectedPart && (
            <Grid item container direction="row" justifyContent="space-around" alignItems="center">
              <Grid item xs={2} textAlign="center">
                <DecreaseButton
                  disabled={tempQty === 1}
                  className={tempQty === 1 ? "buttonDisabled" : ""}
                  onClick={() => setTempQty(tempQty - 1)}
                />
              </Grid>
              <Grid item xs={7} textAlign="center">
                <StyledTextField
                  value={tempQty}
                  id="quantity"
                  type="number"
                  margin="normal"
                  onChange={(event) => changeQtyFromInput(event.target.value)}
                  helperText={qtyError}
                  error={qtyError !== ""}
                />
              </Grid>
              <Grid item xs={2} textAlign="center">
                <IncreaseButton onClick={() => setTempQty(tempQty + 1)} />
              </Grid>
            </Grid>
          )}

          <Grid item>
            <PrimaryButton disabled={selectedPart === null} onClick={addStockCb}>
              <FormattedMessage id="stock.orderStock" />
            </PrimaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton
              variant="contained"
              onClick={closeDialog}
              data-testid="SelectStockDialogFinish"
            >
              {intl.formatMessage({
                id: "general.finish",
              })}
            </SecondaryButton>
          </Grid>
        </Grid>
      </StyledContainer>
      <FullScreenDialog
        title={`${intl.formatMessage({
          id: "filter.setFilters",
        })}`}
        centerTitle
        goingBack
        open={filterDialogOpen}
        setOpen={setFilterDialogOpen}
        child={
          <PartFilterDialog
            filter={filter}
            setFilter={setFilter}
            onReset={() => {
              setFilter(defaultPartFilter);
            }}
            onSubmit={() => {
              setAppliedFilter(filter);
              setFilterDialogOpen(false);
            }}
          />
        }
      />
    </Dialog>
  );
};
